import { routes, versionedRoutes } from '@sketch/modules-common'
import { NotificationInfoFragment } from '@sketch/gql-types'

/**
 * Resolves the in-app destination for a notification's "Go to Document" action.
 * Mirrors the legacy notifications list routing. Returns `null` when there's no
 * navigable target (the caller hides the action in that case).
 */
export const getNotificationPath = (
  notification: NotificationInfoFragment
): string | null => {
  if (notification.__typename === 'DownloadableAssetRenderedNotification') {
    if (!notification.share || !notification.notifiedVersion) return null

    return versionedRoutes.SHARE_VIEW.VERSION.create({
      shareID: notification.share.identifier,
      versionShortId: notification.notifiedVersion.shortId,
    })
  }

  if (
    (notification.__typename === 'CommentNotification' ||
      notification.__typename === 'MentionNotification') &&
    notification.comment
  ) {
    const { annotation } = notification.comment
    const subject = annotation.currentSubject

    if (subject.__typename === 'Artboard' || subject.__typename === 'Page') {
      const subjectId =
        subject.__typename === 'Artboard'
          ? subject.permanentArtboardShortId
          : (subject.uuid ?? '')

      return routes.ANNOTATION_REDIRECT.create({
        shareId: notification.share?.identifier ?? '',
        annotationId: annotation.identifier,
        query: { subjectType: subject.__typename, subjectId },
      })
    }
  }

  if (notification.path) return notification.path

  if (
    notification.__typename === 'CommentNotification' &&
    notification.share &&
    notification.artboard?.permanentArtboardShortId
  ) {
    return routes.ARTBOARD_DETAIL.create({
      shareID: notification.share.identifier,
      permanentArtboardShortId: notification.artboard.permanentArtboardShortId,
    })
  }

  return null
}

/**
 * Resolves the in-app destination for a folder notification's "View folder"
 * action. Only archived, trashed, restored and newly-joined folders still
 * exist — a trashed folder lives in the workspace Trash, while an archived,
 * restored or newly-joined one opens in the regular project view (there's no
 * per-project archive route). Returns `null` when the folder is gone
 * (permanently deleted) or we're missing the ids to build a link.
 */
export const getFolderPath = (
  notification: NotificationInfoFragment
): string | null => {
  if (
    notification.__typename !== 'FolderArchivedNotification' &&
    notification.__typename !== 'FolderTrashedNotification' &&
    notification.__typename !== 'FolderRestoredNotification' &&
    notification.__typename !== 'AddProjectMemberNotification'
  ) {
    return null
  }

  const workspaceId = notification.workspace?.identifier
  const projectId = notification.folder?.identifier
  if (!workspaceId || !projectId) return null

  const route =
    notification.__typename === 'FolderTrashedNotification'
      ? routes.WORKSPACE_TRASH_PROJECT
      : routes.WORKSPACE_PROJECT

  return route.create({ workspaceId, projectId })
}
